"use client";

import { useState } from "react";
import { Mail, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useT, useLocale } from "@/lib/locale-context";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api";

export default function VerifyEmailBanner() {
  const t = useT();
  const { locale } = useLocale();
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  if (!user || user.email_verified !== false) return null;

  const handleResend = async () => {
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ locale }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || t("auth.verify.resend.failed"));
      } else {
        setSent(true);
      }
    } catch {
      setError(t("auth.verify.network"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
      {/* Mobile keeps the banner on one row by dropping the email
          interpolation (it was the main reason the copy overflowed
          and wrapped to 2-3 lines) and by shrinking the body text to
          xs. The "Check your inbox" detail is desktop-only via
          hidden sm:inline so mobile doesn't pay for the extra words. */}
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-2 text-xs sm:text-sm">
        <div className="flex items-center gap-2 text-amber-900 min-w-0">
          <Mail className="w-4 h-4 shrink-0" />
          <span className="truncate">
            {t("auth.verify.banner")}
            <span className="hidden sm:inline">. {t("auth.verify.banner.detail")}</span>
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {error && <span className="text-[11px] text-rose-700 hidden sm:inline">{error}</span>}
          {sent ? (
            <span className="text-xs font-medium text-emerald-700">{t("auth.verify.resend.sent")}</span>
          ) : (
            <button
              onClick={handleResend}
              disabled={sending}
              className="text-xs font-medium text-amber-900 underline hover:text-amber-950 disabled:opacity-50 flex items-center gap-1"
            >
              {sending && <Loader2 className="w-3 h-3 animate-spin" />}
              {t("auth.verify.resend")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
